import React from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material';

const primaryColor = '#5757b0';
const VIRTUALIZED_ROW_HEIGHT = 40;
const VIRTUALIZED_OVERSCAN_ROWS = 8;
const DEFAULT_VIRTUALIZED_HEIGHT = 360;
const VIRTUALIZED_MIN_COLUMN_WIDTH = 110;
const VIRTUALIZED_MAX_COLUMN_WIDTH = 360;
const VIRTUALIZED_WIDTH_SAMPLE_ROWS = 100;
const VIRTUALIZED_WIDTH_SAMPLE_CHARACTERS = 160;

const formatCellValue = (value) => {
  if (value === null || value === undefined) {
    return '-';
  }
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch (error) {
      return String(value);
    }
  }
  return String(value);
};

export const getVirtualizedColumnWidths = (rows, keys) => {
  const longestValues = new Map(
    keys.map((key) => [
      key,
      Math.min(String(key || '').length, VIRTUALIZED_WIDTH_SAMPLE_CHARACTERS),
    ])
  );
  const rowsForSizing = (Array.isArray(rows) ? rows : []).slice(
    0,
    VIRTUALIZED_WIDTH_SAMPLE_ROWS
  );

  rowsForSizing.forEach((row) => {
    if (!row || typeof row !== 'object') return;
    Object.entries(row).forEach(([key, value]) => {
      if (!longestValues.has(key)) return;
      const text = String(formatCellValue(value)).slice(0, VIRTUALIZED_WIDTH_SAMPLE_CHARACTERS);
      const longestLine = text.split(/\r?\n/).reduce(
        (length, line) => Math.max(length, Array.from(line).length),
        0
      );
      longestValues.set(key, Math.max(longestValues.get(key), longestLine));
    });
  });

  return keys.map((key) => Math.min(
    VIRTUALIZED_MAX_COLUMN_WIDTH,
    Math.max(
      VIRTUALIZED_MIN_COLUMN_WIDTH,
      (longestValues.get(key) * 8) + 28
    )
  ));
};

const VirtualizedObjectArrayTable = ({
  rows,
  keys,
  showIndex,
  compact,
  labels,
  height,
}) => {
  const [scrollTop, setScrollTop] = React.useState(0);
  const viewportHeight = Number.isFinite(height) && height > 0
    ? height
    : DEFAULT_VIRTUALIZED_HEIGHT;
  const columnWidths = React.useMemo(
    () => getVirtualizedColumnWidths(rows, keys),
    [keys, rows]
  );
  const indexWidth = showIndex ? 56 : 0;
  const tableWidth = indexWidth + columnWidths.reduce((total, width) => total + width, 0);
  const gridTemplateColumns = [
    ...(showIndex ? [`${indexWidth}px`] : []),
    ...columnWidths.map((width) => `${width}px`),
  ].join(' ');
  const visibleRowCount = Math.ceil(viewportHeight / VIRTUALIZED_ROW_HEIGHT);
  const firstVisibleRow = Math.max(
    0,
    Math.floor(Math.max(0, scrollTop - VIRTUALIZED_ROW_HEIGHT) / VIRTUALIZED_ROW_HEIGHT)
      - VIRTUALIZED_OVERSCAN_ROWS
  );
  const lastVisibleRow = Math.min(
    rows.length,
    firstVisibleRow + visibleRowCount + (VIRTUALIZED_OVERSCAN_ROWS * 2)
  );
  const visibleRows = rows.slice(firstVisibleRow, lastVisibleRow);
  const cellFontSize = compact ? '0.7rem' : '0.8rem';

  React.useEffect(() => {
    setScrollTop(0);
  }, [rows]);

  const cellStyle = {
    boxSizing: 'border-box',
    minWidth: 0,
    padding: '9px 12px',
    borderRight: `1px solid ${primaryColor}14`,
    borderBottom: `1px solid ${primaryColor}14`,
    fontSize: cellFontSize,
    lineHeight: '20px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  };

  return (
    <Box
      component={Paper}
      role="grid"
      data-testid="json-virtualized-scroll"
      aria-rowcount={rows.length + 1}
      aria-colcount={keys.length + (showIndex ? 1 : 0)}
      onScroll={(event) => setScrollTop(event.currentTarget.scrollTop)}
      sx={{
        mt: 2,
        width: '100%',
        height: `${viewportHeight + VIRTUALIZED_ROW_HEIGHT}px`,
        overflow: 'auto',
        boxShadow: 'none',
        border: `1px solid ${primaryColor}20`,
      }}
    >
      <Box sx={{ width: `${Math.max(tableWidth, 1)}px`, minWidth: '100%' }}>
        <Box
          role="row"
          sx={{
            display: 'grid',
            gridTemplateColumns,
            minWidth: `${Math.max(tableWidth, 1)}px`,
            height: `${VIRTUALIZED_ROW_HEIGHT}px`,
            backgroundColor: `${primaryColor}10`,
            color: primaryColor,
            fontWeight: 'bold',
            position: 'sticky',
            top: 0,
            zIndex: 2,
          }}
        >
          {showIndex && (
            <Box role="columnheader" sx={cellStyle}>{labels.index}</Box>
          )}
          {keys.map((key) => (
            <Box key={key} role="columnheader" sx={cellStyle}>{key}</Box>
          ))}
        </Box>
        <Box sx={{ position: 'relative', height: `${rows.length * VIRTUALIZED_ROW_HEIGHT}px` }}>
          {visibleRows.map((item, visibleIndex) => {
            const rowIndex = firstVisibleRow + visibleIndex;
            return (
              <Box
                key={rowIndex}
                role="row"
                aria-rowindex={rowIndex + 2}
                sx={{
                  position: 'absolute',
                  top: `${rowIndex * VIRTUALIZED_ROW_HEIGHT}px`,
                  left: 0,
                  display: 'grid',
                  gridTemplateColumns,
                  width: `${Math.max(tableWidth, 1)}px`,
                  height: `${VIRTUALIZED_ROW_HEIGHT}px`,
                  backgroundColor: rowIndex % 2 === 1 ? '#fafafa' : '#fff',
                }}
              >
                {showIndex && (
                  <Box role="gridcell" sx={{ ...cellStyle, color: '#666' }}>{rowIndex}</Box>
                )}
                {keys.map((key) => (
                  <Box key={key} role="gridcell" sx={cellStyle}>
                    {item && typeof item === 'object' && item[key] !== undefined
                      ? formatCellValue(item[key])
                      : '-'}
                  </Box>
                ))}
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

/**
 * Shared JSON table rendering logic
 * Used by TagTooltip, TimeEventJsonViewer, and other components that need to display JSON as tables
 */
const JsonTableRenderer = ({ 
  data, 
  maxHeight = 200, 
  maxItems = Infinity,
  showIndex = true,
  compact = true,
  disableInternalScroll = false,
  virtualizeRows = false,
  virtualizationThreshold = 200,
  virtualizedHeight = DEFAULT_VIRTUALIZED_HEIGHT,
  labels = {}
}) => {
  const effectiveMaxItems = Number.isFinite(maxItems) ? maxItems : Infinity;
  const tableLabels = {
    index: labels.index || '#',
    key: labels.key || 'Key',
    value: labels.value || 'Value',
    moreItems: labels.moreItems || ((count) => `... and ${count} more items`),
    moreFields: labels.moreFields || ((count) => `... and ${count} more fields`)
  };
  const hasBoundedHeight = !disableInternalScroll && maxHeight !== null && maxHeight !== undefined && maxHeight !== 'none';
  const tableShellSx = {
    mt: 2,
    ...(hasBoundedHeight ? { maxHeight } : {}),
    width: '100%',
    overflowX: hasBoundedHeight ? 'auto' : 'visible',
    overflowY: hasBoundedHeight ? 'auto' : 'visible',
    boxShadow: 'none',
    border: `1px solid ${primaryColor}20`
  };

  const renderTableShell = (content) => {
    if (hasBoundedHeight) {
      return (
        <TableContainer component={Paper} sx={tableShellSx}>
          {content}
        </TableContainer>
      );
    }

    return (
      <Box component={Paper} sx={tableShellSx}>
        {content}
      </Box>
    );
  };

  // Ensure data is parsed if it's a string
  let parsedData = data;
  if (typeof data === 'string') {
    try {
      parsedData = JSON.parse(data);
    } catch (e) {
      // If parsing fails, treat as regular text
      return (
        <Box sx={{
          backgroundColor: '#fff',
          padding: '12px 16px',
          borderRadius: '8px',
          ...(hasBoundedHeight ? { maxHeight } : {}),
          overflow: hasBoundedHeight ? 'auto' : 'visible',
          border: `1px solid ${primaryColor}20`
        }}>
          <Typography variant="caption" sx={{ 
            wordBreak: 'break-word',
            color: '#333',
            fontSize: compact ? '0.75rem' : '0.85rem',
            lineHeight: 1.5
          }}>
            {String(data)}
          </Typography>
        </Box>
      );
    }
  }

  // Check if it's JSON-like data
  if (!parsedData || (typeof parsedData !== 'object' && !Array.isArray(parsedData))) {
    return (
      <Box sx={{
        backgroundColor: '#fff',
        padding: '12px 16px',
        borderRadius: '8px',
        ...(hasBoundedHeight ? { maxHeight } : {}),
        overflow: hasBoundedHeight ? 'auto' : 'visible',
        border: `1px solid ${primaryColor}20`
      }}>
        <Typography variant="caption" sx={{ 
          wordBreak: 'break-word',
          color: '#333',
          fontSize: compact ? '0.75rem' : '0.85rem',
          lineHeight: 1.5
        }}>
          {String(data)}
        </Typography>
      </Box>
    );
  }

  // Render arrays
  if (Array.isArray(parsedData)) {
    // Check if array contains objects and get all unique keys
    const hasObjects = parsedData.some(item => typeof item === 'object' && item !== null);
    
    if (hasObjects) {
      // Discover columns from the complete payload so late-appearing fields are never hidden.
      const seenKeys = new Set();
      const allKeys = [];
      parsedData.forEach((item) => {
        if (typeof item !== 'object' || item === null) return;
        Object.keys(item).forEach((key) => {
          if (seenKeys.has(key)) return;
          seenKeys.add(key);
          allKeys.push(key);
        });
      });

      if (virtualizeRows && parsedData.length > virtualizationThreshold) {
        return (
          <VirtualizedObjectArrayTable
            rows={parsedData}
            keys={allKeys}
            showIndex={showIndex}
            compact={compact}
            labels={tableLabels}
            height={virtualizedHeight}
          />
        );
      }
      
      return renderTableShell(
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {showIndex && (
                <TableCell sx={{ 
                  fontWeight: 'bold', 
                  fontSize: compact ? '0.7rem' : '0.8rem',
                  backgroundColor: `${primaryColor}10`,
                  color: primaryColor,
                  width: '30px'
                }}>
                  {tableLabels.index}
                </TableCell>
              )}
              {allKeys.map(key => (
                <TableCell key={key} sx={{ 
                  fontWeight: 'bold', 
                  fontSize: compact ? '0.7rem' : '0.8rem',
                  backgroundColor: `${primaryColor}10`,
                  color: primaryColor,
                  maxWidth: compact ? '120px' : undefined,
                  overflow: compact ? 'hidden' : 'visible',
                  textOverflow: compact ? 'ellipsis' : 'clip',
                  whiteSpace: compact ? 'nowrap' : 'normal',
                  wordBreak: compact ? 'normal' : 'break-word',
                  minWidth: compact ? 'auto' : '100px'
                }}>
                  {key}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {parsedData.slice(0, effectiveMaxItems).map((item, index) => (
              <TableRow key={index}>
                {showIndex && (
                  <TableCell sx={{ fontSize: compact ? '0.7rem' : '0.8rem', color: '#666' }}>
                    {index}
                  </TableCell>
                )}
                {allKeys.map(key => (
                  <TableCell key={key} sx={{ 
                    fontSize: compact ? '0.7rem' : '0.8rem', 
                    maxWidth: compact ? '150px' : undefined,
                    overflow: compact ? 'hidden' : 'visible',
                    textOverflow: compact ? 'ellipsis' : 'clip',
                    whiteSpace: compact ? 'nowrap' : 'normal',
                    wordBreak: compact ? 'normal' : 'break-word',
                    verticalAlign: 'top'
                  }}>
                    {typeof item === 'object' && item !== null ? (
                      <Typography variant="caption" sx={{ 
                        fontSize: compact ? '0.7rem' : '0.8rem',
                        overflow: compact ? 'hidden' : 'visible',
                        textOverflow: compact ? 'ellipsis' : 'clip',
                        whiteSpace: compact ? 'nowrap' : 'normal',
                        wordBreak: compact ? 'normal' : 'break-word',
                        display: 'block'
                      }}>
                        {item[key] !== undefined ? formatCellValue(item[key]) : '-'}
                      </Typography>
                    ) : (
                      <Typography variant="caption" sx={{ 
                        fontSize: compact ? '0.7rem' : '0.8rem',
                        overflow: compact ? 'hidden' : 'visible',
                        textOverflow: compact ? 'ellipsis' : 'clip',
                        whiteSpace: compact ? 'nowrap' : 'normal',
                        wordBreak: compact ? 'normal' : 'break-word',
                        display: 'block'
                      }}>
                        {String(item)}
                      </Typography>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
            {parsedData.length > effectiveMaxItems && (
              <TableRow>
                <TableCell colSpan={allKeys.length + (showIndex ? 1 : 0)} sx={{ 
                  textAlign: 'center', 
                  fontStyle: 'italic', 
                  color: '#999',
                  fontSize: compact ? '0.65rem' : '0.75rem'
                }}>
                  {tableLabels.moreItems(parsedData.length - effectiveMaxItems)}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      );
    } else {
      // Original simple array rendering for non-object arrays
      return renderTableShell(
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {showIndex && (
                <TableCell sx={{ 
                  fontWeight: 'bold', 
                  fontSize: compact ? '0.7rem' : '0.8rem',
                  backgroundColor: `${primaryColor}10`,
                  color: primaryColor
                }}>
                  {tableLabels.index}
                </TableCell>
              )}
              <TableCell sx={{ 
                fontWeight: 'bold', 
                fontSize: compact ? '0.7rem' : '0.8rem',
                backgroundColor: `${primaryColor}10`,
                color: primaryColor
              }}>
                {tableLabels.value}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {parsedData.slice(0, effectiveMaxItems).map((item, index) => (
              <TableRow key={index}>
                {showIndex && (
                  <TableCell sx={{ fontSize: compact ? '0.7rem' : '0.8rem', color: '#666' }}>
                    {index}
                  </TableCell>
                )}
                  <TableCell sx={{ fontSize: compact ? '0.7rem' : '0.8rem' }}>
                    {formatCellValue(item)}
                </TableCell>
              </TableRow>
            ))}
            {parsedData.length > effectiveMaxItems && (
              <TableRow>
                <TableCell colSpan={showIndex ? 2 : 1} sx={{ 
                  textAlign: 'center', 
                  fontStyle: 'italic', 
                  color: '#999',
                  fontSize: compact ? '0.65rem' : '0.75rem'
                }}>
                  {tableLabels.moreItems(parsedData.length - effectiveMaxItems)}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      );
    }
  } else {
    // Render objects
    return renderTableShell(
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell sx={{ 
              fontWeight: 'bold', 
              fontSize: compact ? '0.7rem' : '0.8rem',
              backgroundColor: `${primaryColor}10`,
              color: primaryColor
            }}>
              {tableLabels.key}
            </TableCell>
            <TableCell sx={{ 
              fontWeight: 'bold', 
              fontSize: compact ? '0.7rem' : '0.8rem',
              backgroundColor: `${primaryColor}10`,
              color: primaryColor
            }}>
              {tableLabels.value}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {Object.entries(parsedData || {}).slice(0, effectiveMaxItems).map(([key, value]) => (
            <TableRow key={key}>
              <TableCell sx={{ fontSize: compact ? '0.7rem' : '0.8rem', fontWeight: '500', color: primaryColor }}>
                {key}
              </TableCell>
              <TableCell sx={{ fontSize: compact ? '0.7rem' : '0.8rem' }}>
                {formatCellValue(value)}
              </TableCell>
            </TableRow>
          ))}
          {Object.keys(parsedData || {}).length > effectiveMaxItems && (
            <TableRow>
              <TableCell colSpan={2} sx={{ 
                textAlign: 'center', 
                fontStyle: 'italic', 
                color: '#999',
                fontSize: compact ? '0.65rem' : '0.75rem'
              }}>
                {tableLabels.moreFields(Object.keys(parsedData || {}).length - effectiveMaxItems)}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    );
  }
};

export default JsonTableRenderer;
